import React from 'react';
import type CmdbLandingPageViewType from '@jira/spa-apps__servicedesk-cmdb-landing-page/src/index.tsx';
import { hasAssetsEntitlement } from '@jira/servicedesk__insight-common-services-workspace-context/src/fetchWorkspaceId.tsx';
import { useAssetsTenantContext } from '@jira/assets-app__assets-tenant-context-provider/src/useAssetsTenantContext.tsx';
import { ScreenRedirect } from '@jira/platform__routing-screen-redirect/src/ui/ScreenRedirect.tsx';
import { LazyPage } from '@jira/platform__spa/src/view/lazy-page/index.tsx';
import { markAsProductBundleReady } from '@jira/platform__spa/src/services/mark-as-product-bundle-ready/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { fg } from '@atlassian/jira-feature-gating';

const LazyAssetsLandingPage = lazyForPaint<typeof CmdbLandingPageViewType>(() =>
	markAsProductBundleReady(
		import(
			/* webpackChunkName: "async-assets-landing-page" */ '@jira/spa-apps__servicedesk-cmdb-landing-page/src/index.tsx'
		),
	),
);

// This component will redirect users from /jira/assets to /jira/assets/for-you
// ScreenRedirect triggers a redirect ui event so we don't need to fire a manual operational event
// We can remove this component once the feature is fully rolled out and frequency of the operational event is 0
const AssetsForYouRedirect = () => <ScreenRedirect to="/jira/assets/for-you" />;

/**
 * This component conditionally redirects to the "For You" page when the feature gate is enabled and the user has Assets entitlement.
 * Otherwise, it renders the standard Assets landing page.
 *
 * @returns The Assets For You redirect or the Assets Landing page component.
 */
export const ConditionalAssetsPage = (): React.JSX.Element => {
	const assetsTenantContext = useAssetsTenantContext();
	const hasAssetsEntitlementEnabled =
		fg('assets_for_you') && hasAssetsEntitlement(assetsTenantContext);

	return hasAssetsEntitlementEnabled ? (
		<AssetsForYouRedirect />
	) : (
		<LazyPage
			Page={LazyAssetsLandingPage}
			pageId="spa-apps/servicedesk/insight/cmdb-landing-page"
			shouldShowSpinner={false}
		/>
	);
};
